import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Empty } from 'antd';

import { Thinning } from '../interfaces';
import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { LrvText } from '../../../common/components/LrvText/LrvText';
import * as optimalHarvestPointSlice from '../optimalHarvestPointSlice';
import { LrvEmpty } from '../../../common/components/LrvEmpty/LrvEmpty';
import { THEME, weightUnits, weightUnitsByCompany } from '../../../config/commons';

import { getBiomass } from './helpers';
import styles from './Thinnings.module.scss';
import { NewThinningForm } from './NewThinningForm';
import { EditThinningForm } from './EditThinningForm';
import { DropdownThinningOptions } from './DropdownThinningOptions';

interface Props {
  theme?: 'light' | 'dark';
  height: number;
}

export const Thinnings = (props: Props) => {
  const { theme = 'light', height } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();

  const { company } = useSelector((state: Store) => state.header);
  const { thinning } = useSelector((state: Store) => state.optimalHarvestPoint);
  
  const isLightTheme = theme === THEME.LIGHT;
  const { thinnings } = thinning;

  const renderThinning = (thinning: Thinning) => {
    const label = `${t('thinnings.day')} ${thinning.day} - ${ getBiomass({ biomass: thinning.biomass, weightUnit: company.weightUnit }) } ${company.weightUnit === weightUnitsByCompany.KILOGRAM ? weightUnits.KG : weightUnits.LB}`;

    return (
      <div key={thinning._id} className={styles.thinning}>
        <div className={styles.thinningInfo}>
          <LrvText
            className={styles.text}
            text={label}
            theme={theme}
          />&nbsp;

          <div className={styles.dropdown}>
            <DropdownThinningOptions thinning={thinning} />
          </div>
        </div>
      </div>
    );
  };

  const renderThinnings = () => {
    if (thinnings.length === 0) {
      return (
        <div
          className={cx(styles.thinnings, styles.center)}
          style={{ maxHeight: height - 42 }}
        >
          <LrvEmpty
            description={t('empty')}
            theme={theme}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    return (
      <div
        className={styles.thinnings}
        style={{ maxHeight: height - 42 }}
      >
        {thinnings.map(renderThinning)}
      </div>
    );
  };

  return (
    <div
      className={cx(styles.container, isLightTheme ? styles.lightContainer : styles.darkContainer)}
      style={{ height }}
    >
      <div
        className={styles.containerTitle}
        onClick={() => {
          dispatch(optimalHarvestPointSlice.setShowNewThinningModal(true));
        }}
      >
        <LrvText
          className={styles.title}
          text={t('thinnings.title')}
          theme={theme}
        />
        
        <div className={styles.addIcon}>
          <Icon name='add' theme={theme} className={styles.icon} />
        </div>
      </div>

      {renderThinnings()}

      <NewThinningForm theme='light'/>
      <EditThinningForm theme='light'/>
    </div>
  );
};
